import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, Polygon } from 'react-leaflet';
import * as mgrs from 'mgrs';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import './App.css';
import logoImg from './logo_einzel.png';
import logoImgWithText from './logo.png';

/* --- LEAFLET CONFIG --- */
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

/* --- CONSTANTS & HELPERS --- */
const MAP_LAYERS = {
  standard: { name: "Standard", url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attribution: '&copy; OpenStreetMap', maxNativeZoom: 19 },
  topo: { name: "Topographie", url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png", attribution: '&copy; OpenTopoMap', maxNativeZoom: 15 },
  satellite: { name: "Satellit", url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attribution: 'Tiles &copy; Esri', maxNativeZoom: 18 }
};

const Icons = {
  Copy: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2-2v1"/></svg>,
  Location: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>,
  Grid: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><path d="M9 3v18M15 3v18M3 9h18M3 15h18"/></svg>,
  Search: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  Spinner: () => <svg className="spinner" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3182ce" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>,
  Burger: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>,
  Close: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>,
  Home: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
};

function MapClickHandler({ onLocationSelected }) { useMapEvents({ click(e) { onLocationSelected(e.latlng.lat, e.latlng.lng); }, }); return null; }

const formatAddress = (addr) => { if (!addr) return ""; const street = addr.road || addr.pedestrian || addr.street || ""; const number = addr.house_number || ""; const streetPart = street ? (number ? `${street} ${number}` : street) : ""; const city = addr.city || addr.town || addr.village || addr.municipality || ""; const zip = addr.postcode || ""; let locality = addr.hamlet || addr.suburb || addr.neighbourhood || addr.locality || ""; if (locality === city) locality = ""; const district = addr.county || addr.district || ""; const state = addr.state || ""; const country = addr.country || ""; let result = []; if (streetPart) result.push(streetPart); if (city) result.push(city); if (zip) result.push(zip); if (locality) result.push(locality); let regionParts = []; if (district) regionParts.push(district); if (state) regionParts.push(state); if (country) regionParts.push(country); if (regionParts.length > 0) result.push(regionParts.join(", ")); return result.join(", "); };
const formatMgrs = (raw) => { if (!raw) return ""; const clean = raw.replace(/\s+/g, '').toUpperCase(); const match = clean.match(/^(\d+[A-Z])([A-Z]{2})(\d+)$/); if (!match) return raw; const half = match[3].length / 2; return `${match[1]} ${match[2]} ${match[3].substring(0, half)} ${match[3].substring(half)}`; };
const calculateGridPolygon = (lat, lon, prec) => { try { const fullStr = mgrs.forward([lon, lat], 5); const clean = fullStr.replace(/\s+/g, ''); const match = clean.match(/^(\d+[A-Z])([A-Z]{2})(\d{10})$/); if (!match) return null; const eastRaw = parseInt(match[3].substring(0, 5), 10); const northRaw = parseInt(match[3].substring(5, 10), 10); const mask = Math.pow(10, 5 - prec); const eFloor = Math.floor(eastRaw / mask) * mask; const nFloor = Math.floor(northRaw / mask) * mask; const pad = (n) => n.toString().padStart(5, '0'); const toPt = (e, n) => mgrs.toPoint(`${match[1]}${match[2]}${pad(e)}${pad(n)}`); const pSW = toPt(eFloor, nFloor); const pNW = toPt(eFloor, nFloor + mask); const pSE = toPt(eFloor + mask, nFloor); const pNE = toPt(eFloor + mask, nFloor + mask); return [[pSW[1], pSW[0]], [pNW[1], pNW[0]], [pNE[1], pNE[0]], [pSE[1], pSE[0]]]; } catch (e) { return null; } };

// Custom Hook for persistent state
function usePersistentState(key, defaultValue) {
  const [state, setState] = useState(() => {
    const saved = localStorage.getItem(key);
    return saved !== null ? JSON.parse(saved) : defaultValue;
  });

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(state));
  }, [key, state]);

  return [state, setState];
}

// Reusable Toggle Component
const ToggleRow = ({ label, icon: Icon, checked, onChange, disabled }) => (
  <div className="toggle-row">
    <div className="toggle-label-content">
      <Icon />
      <span>{label}</span>
    </div>
    <label className="switch">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span className="slider"></span>
    </label>
  </div>
);

/**
 * MAIN COMPONENT
 */
function App() {
  const mapRef = useRef(null);
  
  // App States
  const [isAppLoading, setIsAppLoading] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [hasConsented, setHasConsented] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState('');

  // Location States
  const [lat, setLat] = useState(48.467838);
  const [lon, setLon] = useState(14.164824);
  const [precision, setPrecision] = useState(3);
  const [mgrsInputValue, setMgrsInputValue] = useState('');
  
  // UI States
  const [currentLayer, setCurrentLayer] = useState('standard');
  const [loadingLocation, setLoadingLocation] = useState(false);

  // Persistent Settings
  const [showGrid, setShowGrid] = usePersistentState('planquadrat_show_grid', true);
  const [showAddress, setShowAddress] = usePersistentState('planquadrat_show_address', false);
  
  // Search States
  const [address, setAddress] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  
  const ignoreSearchRef = useRef(false);
  const reverseGeoAbortController = useRef(null);

  // --- LOGIC FUNCTIONS (Moved up for dependencies) ---
  const performReverseGeocoding = useCallback(async (l, lo) => { 
    if (!hasConsented) return; 
    if (reverseGeoAbortController.current) reverseGeoAbortController.current.abort(); 
    const controller = new AbortController(); 
    reverseGeoAbortController.current = controller; 
    
    try { 
        const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${l}&lon=${lo}&accept-language=de&addressdetails=1`; 
        const res = await fetch(url, { headers: { 'User-Agent': 'PlanQuadrat-App' }, signal: controller.signal }); 
        const data = await res.json(); 
        
        if (!controller.signal.aborted) {
          if (data && data.address) { setAddress(formatAddress(data.address)); } 
          else if (data.display_name) { setAddress(data.display_name); } 
          else { setAddress(""); }
          setIsLoadingAddress(false);
        }
      } catch (e) { 
          if (e.name !== 'AbortError') { 
              setIsLoadingAddress(false); 
              setAddress("Adresse nicht gefunden");
          } 
      } 
  }, [hasConsented]);

  // Memos
  const { calculatedMgrs, calculatedGrid, calculationError } = useMemo(() => {
    try {
      if (isNaN(lat) || isNaN(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) {
          return { calculatedMgrs: '', calculatedGrid: null, calculationError: 'Ungültige Koordinaten' };
      }
      const raw = mgrs.forward([lon, lat], precision);
      return {
        calculatedMgrs: formatMgrs(raw),
        calculatedGrid: calculateGridPolygon(lat, lon, precision),
        calculationError: ''
      };
    } catch (e) {
      return { calculatedMgrs: '', calculatedGrid: null, calculationError: 'Fehler bei Berechnung' };
    }
  }, [lat, lon, precision]);

  // Effects
  useEffect(() => {
    const timer = setTimeout(() => setIsAppLoading(false), 1100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const consent = localStorage.getItem('planquadrat_consent');
    if (consent === 'true') setHasConsented(true);
  }, []);

  useEffect(() => {
    if (calculatedMgrs && ignoreSearchRef.current) {
        setMgrsInputValue(calculatedMgrs);
    }
  }, [calculatedMgrs]);

  // Auto-Update Address
  useEffect(() => {
    if (!showAddress) return; 

    let timer;
    if (!isLoadingSearch && ignoreSearchRef.current && hasConsented) {
        setIsLoadingAddress(true);
        setAddress('');
        timer = setTimeout(() => {
           performReverseGeocoding(lat, lon);
        }, 500);
    }
    return () => {
        if(timer) clearTimeout(timer);
        if (reverseGeoAbortController.current) {
            reverseGeoAbortController.current.abort();
        }
    };
  }, [lat, lon, hasConsented, isLoadingSearch, showAddress, performReverseGeocoding]); 

  // Address Search
  useEffect(() => {
    if (address.length < 3 || ignoreSearchRef.current || !hasConsented) {
      setSearchResults([]);
      setIsLoadingSearch(false);
      return;
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      setIsLoadingSearch(true);
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(address)}&accept-language=de&limit=5&addressdetails=1`;
        const res = await fetch(url, { headers: { 'User-Agent': 'PlanQuadrat-App' }, signal: controller.signal });
        const data = await res.json();
        if (!controller.signal.aborted) {
            setSearchResults(data);
            setIsLoadingSearch(false);
        }
      } catch (err) { 
          if (err.name !== 'AbortError') setIsLoadingSearch(false); 
      }
    }, 300);
    return () => { 
        clearTimeout(timeoutId); 
        controller.abort();
        setIsLoadingSearch(false); 
    };
  }, [address, hasConsented]);

  // Handlers
  const handleConsent = () => { localStorage.setItem('planquadrat_consent', 'true'); setHasConsented(true); };
  
  const flyToLocation = (l, lo) => {
    if (mapRef.current) {
      mapRef.current.flyTo([l, lo], 15, { animate: true, duration: 1.5 });
    }
  };

  const handleMapClick = (l, lo) => { 
      ignoreSearchRef.current = true; 
      setSearchResults([]); 
      setLat(l); setLon(lo); 
  };

  const handleLatLonChange = (l, lo) => { 
      ignoreSearchRef.current = true; setSearchResults([]); 
      const latVal = parseFloat(l); const lonVal = parseFloat(lo); 
      setLat(latVal); setLon(lonVal); 
      if (!isNaN(latVal) && !isNaN(lonVal)) {
        flyToLocation(latVal, lonVal);
      }
  };

  const handleAddressInput = (e) => { ignoreSearchRef.current = false; setAddress(e.target.value); };

  const selectSuggestion = (item) => { 
      ignoreSearchRef.current = true; 
      const newLat = parseFloat(item.lat); const newLon = parseFloat(item.lon); 
      setLat(newLat); setLon(newLon); 
      setAddress(item.address ? formatAddress(item.address) : item.display_name); 
      setSearchResults([]); setIsLoadingSearch(false); 
      flyToLocation(newLat, newLon);
  };

  const handleMgrsChange = (e) => { 
      setMgrsInputValue(e.target.value);
      const val = e.target.value.toUpperCase(); 
      const clean = val.replace(/\s+/g, ''); 
      if (clean.length >= 5) { 
          try { 
              const pt = mgrs.toPoint(clean); 
              ignoreSearchRef.current = true; 
              setSearchResults([]); 
              setLat(pt[1]); setLon(pt[0]); 
              flyToLocation(pt[1], pt[0]); 
            } catch (e) {} 
        } 
  };

  const handleGetLocation = () => { 
      if (!navigator.geolocation) return; 
      setLoadingLocation(true); ignoreSearchRef.current = true; setSearchResults([]); 
      navigator.geolocation.getCurrentPosition(pos => { 
          handleMapClick(pos.coords.latitude, pos.coords.longitude); 
          flyToLocation(pos.coords.latitude, pos.coords.longitude);
          setLoadingLocation(false); 
        }, () => { alert("Standort konnte nicht ermittelt werden."); setLoadingLocation(false); }); 
    };

  const copyToClipboard = (txt, lbl) => { navigator.clipboard.writeText(txt); setCopyFeedback(`${lbl} kopiert!`); setTimeout(() => setCopyFeedback(''), 2000); };
  const getPrecisionText = (p) => { const map = {1:'10 km', 2:'1 km', 3:'100 m', 4:'10 m', 5:'1 m'}; return map[p] || ''; };
  const getPlaceholder = () => { const map = {1:"33U UU 8 8", 2:"33U UU 89 81", 3:"33U UU 895 815", 4:"33U UU 8958 8155", 5:"33U UU 89581 81552"}; return "z.B. " + (map[precision] || ""); };


  return (
    <>
      <div className={`loading-overlay ${!isAppLoading ? 'loaded' : ''}`}>
        <img src={logoImgWithText} alt="Loading..." className="loading-logo" />
      </div>

      {copyFeedback && <div className="toast-container"><div className="toast-msg">{copyFeedback}</div></div>}

      <button className="floating-menu-trigger" onClick={() => setIsMobileSidebarOpen(true)}>
          <Icons.Burger />
      </button>

      <div className="app-layout">
        
        <div className={`menu-backdrop ${isMobileSidebarOpen ? 'active' : ''}`} onClick={() => setIsMobileSidebarOpen(false)}></div>

        <aside className={`sidebar ${isMobileSidebarOpen ? 'mobile-open' : ''}`}>
          <button className="icon-btn mobile-close-btn mobile-only" onClick={() => setIsMobileSidebarOpen(false)}>
            <Icons.Close />
          </button>

          <div className="sidebar-logo-container">
            <img src={logoImg} alt="Planquadrat Logo" className="sidebar-logo" />
            <h1>Planquadrat</h1>
            <p>MGRS & WGS84 Umrechner</p>
          </div>

          <div className="sidebar-section">
            <span className="sidebar-label">Werkzeuge</span>
            
            <button onClick={handleGetLocation} className="tool-btn primary-btn" disabled={loadingLocation || !hasConsented} style={{marginBottom:'20px'}}>
              {loadingLocation ? "Suche..." : <><Icons.Location/> Mein Standort</>}
            </button>

            <div className="toggle-group">
                <ToggleRow 
                  label="Adresssuche" 
                  icon={Icons.Home} 
                  checked={showAddress} 
                  onChange={setShowAddress} 
                  disabled={!hasConsented} 
                />
                <ToggleRow 
                  label="Rasterfläche" 
                  icon={Icons.Grid} 
                  checked={showGrid} 
                  onChange={setShowGrid} 
                  disabled={!hasConsented} 
                />
            </div>
          </div>

          <div className="sidebar-section">
            <span className="sidebar-label">Einstellungen</span>
            <div style={{marginBottom: '15px'}}>
               <label className="input-label">Kartenebene</label>
               <select value={currentLayer} onChange={(e) => setCurrentLayer(e.target.value)} disabled={!hasConsented}>
                {Object.keys(MAP_LAYERS).map(k => <option key={k} value={k}>{MAP_LAYERS[k].name}</option>)}
              </select>
            </div>
            <div>
              <label className="input-label">MGRS Genauigkeit</label>
              <select value={precision} disabled={!hasConsented} onChange={(e) => { const p = parseInt(e.target.value); setPrecision(p); }}>
                {[1,2,3,4,5].map(n => <option key={n} value={n}>{getPrecisionText(n)}</option>)}
              </select>
            </div>
          </div>

          <div className="sidebar-footer">
            <a href="https://felum.at/impressum.html" className="legal-link">Impressum</a>
            <a href="/datenschutzerklaerung.html" className="legal-link">Datenschutz</a>
          </div>
        </aside>

        <main className="main-content">
          <div className="card-box">
            {/* MGRS INPUT */}
            <div style={{marginBottom: '20px'}}>
              <label className="input-label">MGRS Koordinate</label>
              <div className="input-wrapper">
                <div className="input-container-relative">
                  <input type="text" value={mgrsInputValue} onChange={handleMgrsChange} placeholder={getPlaceholder()} disabled={!hasConsented} />
                  <button className="copy-btn" onClick={() => copyToClipboard(calculatedMgrs, "MGRS")} title="Kopieren"><Icons.Copy/></button>
                </div>
              </div>
              {calculationError && <small style={{color:'#e53e3e', fontWeight:600, marginTop:'4px', display:'block'}}>{calculationError}</small>}
            </div>

            {/* LAT/LON INPUTS */}
            <div className="input-group" style={{marginBottom: '20px'}}>
              <div className="input-wrapper">
                <label className="input-label">Breitengrad (Lat)</label>
                <div className="input-container-relative">
                  <input type="number" value={lat} onChange={(e) => handleLatLonChange(e.target.value, lon)} step="0.000001" disabled={!hasConsented} />
                </div>
              </div>
              
              <div className="input-wrapper">
                <label className="input-label">Längengrad (Lon)</label>
                <div className="input-container-relative">
                  <input type="number" value={lon} onChange={(e) => handleLatLonChange(lat, e.target.value)} step="0.000001" disabled={!hasConsented} />
                  <button className="copy-btn" onClick={() => copyToClipboard(`${lat}, ${lon}`, "GPS")} title="Kopieren"><Icons.Copy/></button>
                </div>
              </div>
            </div>

            {/* CONDITIONAL ADDRESS SEARCH */}
            {showAddress && (
                <div>
                <label className="input-label">Adresse / Ortssuche</label>
                <div style={{position: 'relative'}}>
                    <span className="search-icon-pos">{(isLoadingSearch || isLoadingAddress) ? <Icons.Spinner/> : <Icons.Search/>}</span>
                    <input type="text" value={address} onChange={handleAddressInput} placeholder="Suche oder Klick auf Karte..." style={{paddingLeft: '40px', paddingRight: address ? '40px' : '16px'}} disabled={!hasConsented} onBlur={() => setTimeout(() => setSearchResults([]), 200)} />
                    {address && (<button className="copy-btn" onClick={() => copyToClipboard(address, "Adresse")} title="Kopieren"><Icons.Copy/></button>)}
                    {searchResults.length > 0 && (<ul className="suggestions-list">{searchResults.map((item) => (<li key={item.place_id} className="suggestion-item" onClick={() => selectSuggestion(item)}>{item.display_name}</li>))}</ul>)}
                </div>
                </div>
            )}
          </div>

          <div className="map-container-card" style={{ flex: 1, height: 'auto', minHeight: '400px' }}>
            {hasConsented ? (
              <MapContainer ref={mapRef} center={[lat, lon]} zoom={15} style={{ height: "100%", width: "100%" }}>
                <MapClickHandler onLocationSelected={handleMapClick} />
                <TileLayer key={currentLayer} attribution={MAP_LAYERS[currentLayer].attribution} url={MAP_LAYERS[currentLayer].url} maxNativeZoom={MAP_LAYERS[currentLayer].maxNativeZoom} maxZoom={19} />
                {showGrid && calculatedGrid && (<Polygon positions={calculatedGrid} pathOptions={{ color: '#ff7800', weight: 2, fillOpacity: 0.15 }} />)}
                <Marker position={[lat, lon]}>
                    <Popup>
                        <strong>MGRS:</strong> {calculatedMgrs}
                        {/* Only show address in popup if enabled */}
                        {showAddress && (
                             <>
                             <br/>
                             <div style={{borderTop:'1px solid #eee', marginTop:5, paddingTop:5, fontSize:'0.9em'}}>
                                 {isLoadingAddress ? <i>Lade Adresse...</i> : address}
                             </div>
                             </>
                        )}
                    </Popup>
                </Marker>
              </MapContainer>
            ) : (
              <div style={{height:'100%', width:'100%', background:'#f1f5f9', display:'flex', justifyContent:'center', alignItems:'center', color:'var(--text-muted)', fontWeight:500}}>
                Karte deaktiviert bis zur Zustimmung
              </div>
            )}
          </div>
        </main>
      </div>
      
      {!hasConsented && !isAppLoading && (
        <div className="consent-overlay">
          <div className="consent-box">
            <h2>Datenschutz Hinweis</h2>
            <p>Wir nutzen Kartendienste von Drittanbietern (OpenStreetMap, Esri). Ihre IP-Adresse wird zur Darstellung übertragen. Satellitenbilder kommen von Servern in den USA.</p>
            <button className="tool-btn primary-btn" onClick={handleConsent}>Akzeptieren & Karte laden</button>
            <div style={{marginTop: '20px', display:'flex', gap:'15px', justifyContent:'center'}}>
                <a href="https://felum.at/impressum.html" className="legal-link">Impressum</a>
                <a href="/datenschutzerklaerung.html" className="legal-link">Datenschutz</a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default App;